<script setup>
import { useLayouts } from '@layouts';
import { VerticalNavLink } from '@layouts/components';
import { config } from '@layouts/config';
import { can, canViewNavMenuGroup } from '@layouts/plugins/casl';

import { useNavItemVisibility } from '@/composables/useNavItemVisibility';
import { buildGroupKey, injectionKeyNavGroupKey } from '@/helpers/navGroups';
import { useNavGroupsStore } from '@/stores/navGroups';

const props = defineProps({
  item: {
    type: null,
    required: true,
  },
});

defineOptions({ name: 'VerticalNavGroup' });

// envOnly gating for groups — `canViewNavMenuGroup` only checks CASL child visibility,
// so without this an env-only group (Inventory, Finance, Alerts, Dev Tools …) would
// still render its header when the effective environment hides all its items (e.g.
// while simulating production). Flag semantics live in useNavItemVisibility; the
// `channel` gate (marketplace connected for this business profile) is applied alongside it.
const { isEnvAllowed, isChannelAllowed, isDevOnly } = useNavItemVisibility();

// A child is visible only when it passes BOTH env and CASL (recursing into sub-groups),
// so a group with no actually-visible child collapses away instead of showing empty.
// Called directly from the template v-if (never wrapped in a computed): `can()` relies
// on getCurrentInstance(), which is only valid during render — caching it in a computed
// makes it re-evaluate outside render context on navigation and wrongly return false.
const isChildVisible = (child) => {
  if (!isEnvAllowed(child) || !isChannelAllowed(child)) return false;
  if ('children' in child) return child.children.some(isChildVisible);
  return can(child.action, child.subject);
};

const { width: windowWidth } = useWindowSize();
const { isVerticalNavMini, dynamicI18nProps } = useLayouts();
const hideTitleAndBadge = isVerticalNavMini(windowWidth);

// Open/closed state lives in the navGroups store, keyed by the titles of all ancestors
// joined with `/`: titles repeat across marketplaces, so a bare title is not unique.
const navGroups = useNavGroupsStore();
const parentKey = inject(injectionKeyNavGroupKey, '');
const groupKey = buildGroupKey(parentKey, props.item.title);
provide(injectionKeyNavGroupKey, groupKey);

const isGroupOpen = computed(() => navGroups.isOpen(groupKey));
const isGroupActive = computed(() => navGroups.currentPathKeys.has(groupKey));
const toggleGroup = () => navGroups.toggle(groupKey);
</script>

<template>
  <li
    v-if="isEnvAllowed(item) && isChannelAllowed(item) && canViewNavMenuGroup(item) && item.children.length && item.children.some(isChildVisible)"
    class="nav-group"
    :class="[
      {
        active: isGroupActive,
        open: isGroupOpen,
        disabled: item.disable,
        'nav-item-dev-only': isDevOnly(item),
      },
    ]"
  >
    <div
      class="nav-group-label"
      @click="toggleGroup"
    >
      <Component
        :is="config.app.iconRenderer || 'div'"
        v-bind="item.icon || config.verticalNav.defaultNavItemIconProps"
        class="nav-item-icon"
      />
      <TransitionGroup name="transition-slide-x">
        <!-- 👉 Title -->
        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-bind="dynamicI18nProps(item.title, 'span')"
          v-show="!hideTitleAndBadge"
          key="title"
          class="nav-item-title"
        >
          {{ item.title }}
        </Component>

        <!-- 👉 Badge -->
        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-bind="dynamicI18nProps(item.badgeContent, 'span')"
          v-show="!hideTitleAndBadge"
          v-if="item.badgeContent"
          key="badge"
          class="nav-item-badge"
          :class="item.badgeClass"
        >
          {{ item.badgeContent }}
        </Component>
        <Component
          :is="config.app.iconRenderer || 'div'"
          v-show="!hideTitleAndBadge && !item.disable"
          v-bind="config.icons.chevronRight"
          key="arrow"
          class="nav-group-arrow"
        />
        <div
          v-if="!hideTitleAndBadge && item.disable"
          class="nav-group-lock-icon-wrap"
        >
          <VTooltip location="top center">
            <div class="tooltip-content">
              <p>{{ $t('Coming Soon') }}</p>
            </div>
            <template #activator="{ props }">
              <span v-bind="props">
                <Component
                  :is="config.app.iconRenderer || 'div'"
                  v-bind="config.icons.lock"
                  key="arrow"
                  class="nav-group-lock-icon"
                />
              </span>
            </template>
          </VTooltip>
        </div>
      </TransitionGroup>
    </div>
    <ul
      v-show="isGroupOpen"
      class="nav-group-children"
    >
      <Component
        :is="'children' in child ? 'VerticalNavGroup' : VerticalNavLink"
        v-for="child in item.children"
        v-bind="child.icon || config.verticalNav.defaultNavItemIconProps"
        :key="child.title"
        :item="child"
      />
    </ul>
  </li>
</template>

<style lang="scss">
.layout-vertical-nav {
  .nav-group {
    &-label {
      display: flex;
      align-items: center;
      cursor: pointer;
    }
  }
}
</style>
